import { useCallback, useMemo } from "react";
import type { InboxSubject } from "../context/InboxContext";

interface UseEmbeddedInboxContextParams {
  conversationId?: string;
  /** Set when the contact has no conversation yet — without it the pane renders nothing. */
  draftContactId?: number;
  unreadCount?: number;
  /** Which application the chat is about, so the SMS mirror reaches this applicant. */
  subject?: InboxSubject;
  /** Called when the first send of a draft promotes it to a real conversation. */
  onConversationResolved: (conversationId: string) => void;
}

/**
 * The InboxProvider value for a conversation rendered OUTSIDE the /inbox route
 * (the floating message pop-up, CRM-1133).
 *
 * `useInboxContext` throws without a provider and its value is not partial, so
 * every member has to be supplied. The page-chrome ones are deliberately inert:
 * the pop-up has no conversation list and no activity panel, and it must never
 * touch the host page's URL the way InboxLayout does.
 */
export const useEmbeddedInboxContext = ({
  conversationId,
  draftContactId,
  unreadCount = 0,
  subject,
  onConversationResolved
}: UseEmbeddedInboxContextParams) => {
  // Called by ComposerForm / SendEmailTemplateDialog after the first send of a
  // draft. On /inbox this writes `?c=` into the URL; here it must only swap the
  // pop-up's own conversation, or it would rewrite the host page's address.
  const selectConversation = useCallback(
    (id: string | undefined) => {
      if (id) onConversationResolved(id);
    },
    [onConversationResolved]
  );

  const state = useMemo(
    () => ({
      selectedConversationId: conversationId,
      // Anchor mode disables the jump-to-bottom and suppresses the infinite
      // scroll sentinels, and the pop-up never deep-links to a message.
      targetMessageId: undefined,
      // Both `true` so DetailHeader's page-chrome buttons stay hidden even if
      // something inside the pane ever renders it.
      showActivityPanel: true,
      showConversationList: true,
      initialUnreadCount: unreadCount,
      draftContactId,
      selectedSubject: subject
    }),
    [conversationId, unreadCount, draftContactId, subject]
  );

  const actions = useMemo(
    () => ({
      selectConversation,
      selectDraftContact: () => {},
      clearDraft: () => {},
      // MUST exist: the composer calls it on every channel change.
      clearTargetMessage: () => {},
      toggleList: () => {},
      setShowDetails: () => {},
      setSelectedSubject: () => {}
    }),
    [selectConversation]
  );

  return useMemo(() => ({ state, actions }), [state, actions]);
};
